import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Search, Users, CreditCard,
  ChevronLeft, ChevronRight, Car, Package as PackageIcon, TrendingUp,
  Link2, Copy, Check as CheckIcon, Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_RES = ["new", "confirmed", "completed", "cancelled"];
const STATUS_LABEL = { new: "Nueva", confirmed: "Confirmada", completed: "Completada", cancelled: "Cancelada", paid: "Pagado", checkout_created: "Pendiente", failed: "Fallido", pending: "Pendiente", canceled: "Cancelado", refunded: "Reembolsado" };
const STATUS_COLOR = {
  new: "bg-blue-100 text-blue-800", confirmed: "bg-teal-100 text-teal-800", completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-700", paid: "bg-emerald-100 text-emerald-800", checkout_created: "bg-amber-100 text-amber-800",
  pending: "bg-amber-100 text-amber-800", failed: "bg-red-100 text-red-700", canceled: "bg-red-100 text-red-700",
  refunded: "bg-amber-100 text-amber-800",
};
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const DEFAULT_LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_paradise-shuttle-mx/artifacts/da39d5c78707bd62_Captura%20de%20pantalla%202026-10-09%20020512.png";
const PANEL_DEFAULTS = { page_bg: "#FFFFFF", button_bg: "#00A896", text_color: "#000000", banner_bg: "#FFFFFF" };
const TPL_DEFAULTS = {
  subject: "", heading: "", intro: "", closing: "",
  brand_name: "XCARET MEXICO", footer: "Enviado por XCARET MEXICO.",
  show_logo: true, logo_url: DEFAULT_LOGO,
  email_bg: "#FDFBF7", card_bg: "#FFFFFF", banner_bg: "#0F2922", banner_text: "#FDFBF7",
  heading_color: "#0F2922", text_color: "#5C756B", accent_color: "#00A896",
};
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => String(a + i));

const contrast = (hex) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#000000";
  const n = parseInt(m[1], 16);
  const lum = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
  return lum > 150 ? "#000000" : "#FFFFFF";
};

const Badge = ({ status }) => (
  <span className={`px-2.5 py-1 rounded-full font-body text-xs ${STATUS_COLOR[status] || "bg-black/10 text-black/60"}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

const ColorField = ({ label, value, onChange, testid }) => (
  <div className="flex items-center gap-3" data-testid={`${testid}-row`}>
    <span className="font-body text-sm flex-1">{label}</span>
    <input type="color" value={HEX_RE.test(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-10 h-9 bg-transparent border p-line cursor-pointer" />
    <Input value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-hex`} className="p-input w-28 h-9 font-mono text-xs" />
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [ps, setPs] = useState(PANEL_DEFAULTS);
  const [psSaving, setPsSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "cashier" });
  const [pwUser, setPwUser] = useState({});
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [resQ, setResQ] = useState("");
  const [payQ, setPayQ] = useState("");
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [events, setEvents] = useState([]);
  const [calQ, setCalQ] = useState("");

  const GL_INIT = { customer_name: "", adults: "1", minors: "0", infants: "0", transport: "", customer_email: "", customer_phone: "", check_in: "", check_out: "", additional_services: "", amount: "", lang: "es" };
  const [gl, setGl] = useState(GL_INIT);
  const [glLink, setGlLink] = useState(null);
  const [glLoading, setGlLoading] = useState(false);
  const setGlField = (k) => (e) => setGl({ ...gl, [k]: e.target.value });

  const generateLink = async () => {
    if (!gl.customer_name || !gl.customer_email || !gl.customer_phone || !gl.amount) {
      toast.error("Completa nombre, correo, teléfono y monto.");
      return;
    }
    if (Number(gl.amount) > 2500 || Number(gl.amount) <= 0) {
      toast.error("El monto debe ser mayor a 0 y máximo $2,500 USD.");
      return;
    }
    setGlLoading(true);
    setGlLink(null);
    try {
      const { data } = await api.post("/admin/payment-links", {
        customer_name: gl.customer_name,
        adults: parseInt(gl.adults, 10) || 0,
        minors: parseInt(gl.minors, 10) || 0,
        infants: parseInt(gl.infants, 10) || 0,
        transport: gl.transport || null,
        customer_email: gl.customer_email,
        customer_phone: gl.customer_phone,
        check_in: gl.check_in || null,
        check_out: gl.check_out || null,
        additional_services: gl.additional_services || null,
        amount: Number(gl.amount),
        lang: gl.lang,
      });
      setGlLink(data.purchase_url);
      setGl(GL_INIT);
      loadOrders();
      toast.success("¡Link de pago generado!");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "No se pudo generar el link.");
    } finally {
      setGlLoading(false);
    }
  };

  const copyLink = () => { navigator.clipboard.writeText(glLink); toast.success("Link copiado"); };
  const glLabel = "font-body text-xs uppercase tracking-wider p-mut mb-1.5 block";
  const glInput = "p-input";

  const [tpl, setTpl] = useState(TPL_DEFAULTS);
  const [tplSaving, setTplSaving] = useState(false);
  const setTplField = (k) => (e) => setTpl({ ...tpl, [k]: e.target.value });
  const TPL_COLOR_KEYS = ["email_bg", "card_bg", "banner_bg", "banner_text", "heading_color", "text_color", "accent_color"];
  const saveTemplate = async () => {
    if (!tpl.subject.trim() || !tpl.heading.trim()) {
      toast.error("El asunto y el título no pueden estar vacíos.");
      return;
    }
    for (const k of TPL_COLOR_KEYS) {
      if (!HEX_RE.test(tpl[k])) { toast.error("Revisa los colores: deben ser código HEX como #00A896."); return; }
    }
    if (tpl.show_logo && tpl.logo_url && !tpl.logo_url.toLowerCase().startsWith("https://")) {
      toast.error("El logo debe ser una URL https://");
      return;
    }
    setTplSaving(true);
    try {
      await api.put("/admin/email-template", tpl);
      toast.success("Plantilla guardada");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "No se pudo guardar la plantilla.");
    } finally {
      setTplSaving(false);
    }
  };
  const tplSample = { nombre: "Carlos Méndez", monto: "$1,800.00 USD", huespedes: "2 adultos, 1 menores", pasajeros: "2 adultos, 1 menores", checkin: "2026-08-20", checkout: "2026-08-27", servicio: "Reservación personalizada" };
  const fillSample = (text) => {
    let s = text || "";
    Object.entries(tplSample).forEach(([k, v]) => { s = s.split("{" + k + "}").join(v); });
    return s;
  };

  const savePanelSettings = async () => {
    for (const k of ["page_bg", "button_bg", "text_color", "banner_bg"]) {
      if (!HEX_RE.test(ps[k])) { toast.error("Revisa los colores: deben ser código HEX como #00A896."); return; }
    }
    setPsSaving(true);
    try { await api.put("/admin/panel-settings", ps); toast.success("Colores actualizados"); }
    catch (e) { toast.error("No se pudieron guardar los colores"); }
    finally { setPsSaving(false); }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.name || newUser.password.length < 6) {
      toast.error("Completa nombre, correo y contraseña (mín. 6 caracteres).");
      return;
    }
    try {
      await api.post("/admin/users", newUser);
      toast.success("Usuario creado");
      setNewUser({ email: "", name: "", password: "", role: "cashier" });
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "No se pudo crear el usuario.");
    }
  };

  const changePassword = async (id) => {
    const pw = pwUser[id];
    if (!pw || pw.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres."); return; }
    try { await api.patch(`/admin/users/${id}/password`, { password: pw }); toast.success("Contraseña actualizada"); setPwUser({ ...pwUser, [id]: "" }); }
    catch (e) { toast.error("No se pudo actualizar la contraseña."); }
  };

  const removeUser = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("Usuario eliminado"); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString?.() || "No se pudo eliminar."); }
  };

  const cancelOrder = async (id) => {
    try { await api.post(`/admin/orders/${id}/cancel`); toast.success("Orden cancelada"); loadOrders(payQ); }
    catch (e) { toast.error(e.response?.data?.detail?.toString?.() || "No se pudo cancelar."); }
  };

  const refundOrder = async (id) => {
    try { await api.post(`/admin/orders/${id}/refund`); toast.success("Pago reembolsado"); loadOrders(payQ); }
    catch (e) { toast.error(e.response?.data?.detail?.toString?.() || "No se pudo reembolsar."); }
  };

  useEffect(() => {
    if (!user) { navigate("/admin/login"); return; }
    if (isAdmin) api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => { if (e.response?.status === 401) { logout(); navigate("/admin/login"); } });
  }, [user]);

  const loadReservations = (q = "") => api.get("/admin/reservations", { params: { q } }).then(({ data }) => setReservations(data)).catch(() => {});
  const loadOrders = (q = "") => api.get("/admin/orders", { params: { q } }).then(({ data }) => setOrders(data)).catch(() => {});
  const loadUsers = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  const loadCalendar = () => {
    const mm = `${month.y}-${String(month.m + 1).padStart(2, "0")}`;
    api.get("/admin/calendar", { params: { month: mm } }).then(({ data }) => setEvents(data.events || [])).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    loadOrders();
    if (isAdmin) { loadReservations(); loadUsers(); }
    api.get("/admin/panel-settings").then(({ data }) => setPs({ ...PANEL_DEFAULTS, ...data })).catch(() => {});
  }, [user]);
  useEffect(() => { if (user && isAdmin) loadCalendar(); }, [user, month]);
  useEffect(() => {
    if (!user) return;
    api.get("/admin/email-template").then(({ data }) => setTpl({ ...TPL_DEFAULTS, ...data })).catch(() => {});
  }, [user]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/reservations/${id}`, { status });
    loadReservations(resQ);
  };

  const changeMonth = (delta) => setMonth((p) => {
    let m = p.m + delta, y = p.y;
    if (m < 0) { m = 11; y -= 1; } if (m > 11) { m = 0; y += 1; }
    return { y, m };
  });

  const calendarCells = useMemo(() => {
    const first = new Date(month.y, month.m, 1);
    const startIdx = (first.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startIdx; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [month]);

  const filteredEvents = useMemo(() => {
    if (!calQ) return events;
    const q = calQ.toLowerCase();
    return events.filter((e) => [e.id, e.name, e.title].some((v) => (v || "").toLowerCase().includes(q)));
  }, [events, calQ]);

  const eventsByDay = (day) => {
    const ds = `${month.y}-${String(month.m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return filteredEvents.filter((e) => e.date === ds);
  };

  const filteredOrders = useMemo(() => {
    if (!payQ) return orders;
    const q = payQ.toLowerCase();
    return orders.filter((o) => [o.id, o.customer_name, o.customer_email, o.customer_phone, o.package_name].some((v) => (v || "").toLowerCase().includes(q)));
  }, [orders, payQ]);

  if (!user) return null;

  const statCards = stats ? [
    { label: "Reservas", value: stats.reservations, icon: Users },
    { label: "Órdenes", value: stats.orders, icon: PackageIcon },
    { label: "Pagadas", value: stats.paid_orders, icon: CreditCard },
    { label: "Ingresos", value: `$${(stats.revenue || 0).toLocaleString()}`, icon: TrendingUp },
  ] : [];

  const bannerText = contrast(ps.banner_bg);
  const btnText = contrast(ps.button_bg);

  return (
    <div
      id="admin-root"
      className="min-h-screen"
      style={{
        "--pbg": ps.page_bg, "--ptext": ps.text_color, "--pbtn": ps.button_bg, "--pbanner": ps.banner_bg,
        "--pbtntext": btnText,
        "--psurf": `color-mix(in srgb, ${ps.text_color} 5%, ${ps.page_bg})`,
        "--pborder": `color-mix(in srgb, ${ps.text_color} 15%, ${ps.page_bg})`,
        "--pmut": `color-mix(in srgb, ${ps.text_color} 55%, ${ps.page_bg})`,
        background: ps.page_bg, color: ps.text_color,
      }}
    >
      <style>{`
        #admin-root .p-card{background:var(--psurf);border:1px solid var(--pborder)}
        #admin-root .p-mut{color:var(--pmut)}
        #admin-root .p-accent{color:var(--pbtn)}
        #admin-root .p-line{border-color:var(--pborder) !important}
        #admin-root .p-input{background:var(--psurf) !important;border-color:var(--pborder) !important;color:var(--ptext) !important;border-radius:0}
        #admin-root .p-input::placeholder{color:var(--pmut)}
        #admin-root .p-btn{background:var(--pbtn);color:var(--pbtntext)}
        #admin-root .p-btn:hover{filter:brightness(0.92)}
        #admin-root .p-row:hover{background:var(--psurf)}
        #admin-root [role=tab]{color:var(--ptext);font-weight:700;background:transparent;border-radius:0;box-shadow:none}
        #admin-root [role=tab]:hover{background:var(--psurf)}
        #admin-root [role=tab][data-state=active]{background:var(--pbtn) !important;color:var(--pbtntext) !important;text-decoration:underline;text-underline-offset:4px;text-decoration-thickness:2px}
        #admin-root .evt-paid{background:rgba(16,185,129,.18);color:#047857;box-shadow:inset 0 0 0 1px rgba(16,185,129,.45)}
        #admin-root .evt-pkg{background:color-mix(in srgb, var(--pbtn) 15%, transparent);color:var(--pbtn)}
        #admin-root .evt-tr{background:rgba(59,130,246,.15);color:#1d4ed8}
        #admin-root .paid-row{background:rgba(16,185,129,.10);border-left:2px solid #34d399}
      `}</style>
      <header
        className="px-6 md:px-10 py-4 flex items-center justify-between sticky top-0 backdrop-blur z-30"
        style={{ background: ps.banner_bg, borderBottom: `1px solid color-mix(in srgb, ${bannerText} 15%, transparent)`, color: bannerText }}
      >
        <Logo variant={bannerText === "#FFFFFF" ? "light" : "dark"} />
        <div className="flex items-center gap-4">
          <span className="font-body text-sm hidden sm:block" style={{ color: `color-mix(in srgb, ${bannerText} 70%, transparent)` }}>
            {user.email}
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs uppercase" style={{ background: `color-mix(in srgb, ${bannerText} 12%, transparent)` }}>{isAdmin ? "Admin" : "Cajero"}</span>
          </span>
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 px-4 py-2 font-body text-sm hover:opacity-70 transition-opacity"
            style={{ border: `1px solid color-mix(in srgb, ${bannerText} 30%, transparent)`, color: bannerText }}
          >
            <LogOut size={15} /> Salir
          </button>
        </div>
      </header>

      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto">
        <h1 className="font-heading text-4xl font-semibold mb-6">Dashboard</h1>

        {isAdmin && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {statCards.map((s) => (
            <div key={s.label} className="p-card p-5" data-testid={`stat-${s.label}`}>
              <s.icon size={20} className="p-accent mb-3" />
              <div className="font-heading text-3xl font-semibold">{s.value}</div>
              <div className="font-body text-xs uppercase tracking-wider p-mut mt-1">{s.label}</div>
            </div>
          ))}
        </div>
        )}

        <Tabs defaultValue={isAdmin ? "reservations" : "generate"}>
          <TabsList className="p-card flex-wrap h-auto">
            {isAdmin && <TabsTrigger value="reservations" data-testid="tab-reservations">Reservas</TabsTrigger>}
            <TabsTrigger value="payments" data-testid="tab-payments">Pagos</TabsTrigger>
            {isAdmin && <TabsTrigger value="calendar" data-testid="tab-calendar">Calendario</TabsTrigger>}
            <TabsTrigger value="generate" data-testid="tab-generate">Generar link</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">Plantillas</TabsTrigger>
            {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Usuarios</TabsTrigger>}
            <TabsTrigger value="color" data-testid="tab-color">Color</TabsTrigger>
          </TabsList>

          {/* RESERVATIONS */}
          <TabsContent value="reservations" className="mt-6">
            <div className="relative max-w-md mb-5">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 p-mut" />
              <Input data-testid="res-search" value={resQ} onChange={(e) => { setResQ(e.target.value); loadReservations(e.target.value); }} placeholder="Buscar por ID, nombre, correo o teléfono" className="p-input pl-9" />
            </div>
            <div className="overflow-x-auto border p-line">
              <table className="w-full font-body text-sm">
                <thead className="p-mut text-xs uppercase tracking-wider" style={{ background: "var(--psurf)" }}>
                  <tr>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Ruta</th>
                    <th className="text-left px-4 py-3">Fecha</th>
                    <th className="text-left px-4 py-3">Pax</th>
                    <th className="text-left px-4 py-3">Vehículo</th>
                    <th className="text-left px-4 py-3">Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((r) => (
                    <tr key={r.id} className="border-t p-line p-row" data-testid={`res-row-${r.id}`}>
                      <td className="px-4 py-3"><div className="font-medium">{r.name}</div><div className="p-mut text-xs">{r.phone}{r.email ? ` · ${r.email}` : ""}</div></td>
                      <td className="px-4 py-3">{r.origin} → {r.destination}</td>
                      <td className="px-4 py-3">{r.date}{r.time ? ` ${r.time}` : ""}</td>
                      <td className="px-4 py-3">{r.adults || r.passengers}A {r.children ? `${r.children}M` : ""}</td>
                      <td className="px-4 py-3">{r.vehicle || "—"}</td>
                      <td className="px-4 py-3">
                        <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger className="p-input h-8 w-36 text-xs" data-testid={`res-status-${r.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS_RES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                  {reservations.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center p-mut">Sin reservas</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* PAYMENTS */}
          <TabsContent value="payments" className="mt-6">
            <div className="relative max-w-md mb-5">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 p-mut" />
              <Input data-testid="pay-search" value={payQ} onChange={(e) => setPayQ(e.target.value)} placeholder="Buscar por ID, nombre, correo o teléfono" className="p-input pl-9" />
            </div>
            <div className="overflow-x-auto border p-line">
              <table className="w-full font-body text-sm">
                <thead className="p-mut text-xs uppercase tracking-wider" style={{ background: "var(--psurf)" }}>
                  <tr>
                    <th className="text-left px-4 py-3">Orden</th>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Paquete</th>
                    <th className="text-left px-4 py-3">Monto</th>
                    <th className="text-left px-4 py-3">Fecha servicio</th>
                    <th className="text-left px-4 py-3">Pago</th>
                    {isAdmin && <th className="text-left px-4 py-3">Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => (
                    <tr key={o.id} className={`border-t p-line ${o.status === "paid" ? "paid-row" : "p-row"}`} data-testid={`order-row-${o.id}`}>
                      <td className="px-4 py-3 p-mut text-xs font-mono">{o.id.slice(0, 8)}</td>
                      <td className="px-4 py-3"><div className="font-medium">{o.customer_name}</div><div className="p-mut text-xs">{o.customer_phone}{o.customer_email ? ` · ${o.customer_email}` : ""}</div></td>
                      <td className="px-4 py-3">{o.package_name}</td>
                      <td className="px-4 py-3 font-semibold p-accent">${(o.amount || 0).toLocaleString()} USD</td>
                      <td className="px-4 py-3">{o.service_date || "—"}</td>
                      <td className="px-4 py-3"><Badge status={o.status} /></td>
                      {isAdmin && (
                        <td className="px-4 py-3">
                          {o.status === "paid" ? (
                            <button onClick={() => refundOrder(o.id)} data-testid={`refund-${o.id}`} className="text-xs border border-amber-500/50 text-amber-700 px-3 py-1.5 hover:bg-amber-500/10 transition-colors">Reembolsar</button>
                          ) : (o.status === "refunded" || o.status === "cancelled") ? (
                            <span className="text-xs p-mut">—</span>
                          ) : (
                            <button onClick={() => cancelOrder(o.id)} data-testid={`cancel-${o.id}`} className="text-xs border border-red-500/50 text-red-600 px-3 py-1.5 hover:bg-red-500/10 transition-colors">Cancelar</button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {filteredOrders.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-10 text-center p-mut">Sin órdenes de pago todavía</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CALENDAR */}
          <TabsContent value="calendar" className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
              <div className="flex items-center gap-3">
                <button onClick={() => changeMonth(-1)} data-testid="cal-prev" className="p-2 border p-line p-row"><ChevronLeft size={16} /></button>
                <span className="font-heading text-2xl font-semibold w-48 text-center" data-testid="cal-month-label">{MONTHS[month.m]} {month.y}</span>
                <button onClick={() => changeMonth(1)} data-testid="cal-next" className="p-2 border p-line p-row"><ChevronRight size={16} /></button>
              </div>
              <div className="relative max-w-xs w-full">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 p-mut" />
                <Input data-testid="cal-search" value={calQ} onChange={(e) => setCalQ(e.target.value)} placeholder="Buscar por ID, nombre o servicio" className="p-input pl-9" />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-px border p-line" style={{ background: "var(--pborder)" }} data-testid="calendar-grid">
              {DAYS.map((d) => <div key={d} className="py-2 text-center font-body text-xs uppercase tracking-wider p-mut" style={{ background: "var(--pbg)" }}>{d}</div>)}
              {calendarCells.map((day, i) => (
                <div key={i} className="min-h-[96px] p-1.5 align-top" style={{ background: "var(--pbg)" }}>
                  {day && (
                    <>
                      <div className="font-body text-xs p-mut mb-1">{day}</div>
                      <div className="space-y-1">
                        {eventsByDay(day).map((e, k) => {
                          const paid = e.status === "paid";
                          const cls = paid ? "evt-paid" : e.kind === "package" ? "evt-pkg" : "evt-tr";
                          return (
                            <div key={k} className={`px-1.5 py-1 rounded text-[10px] leading-tight ${cls}`} title={`${e.title} — ${e.name}${paid ? " (Pagado)" : ""}`} data-testid={`cal-event-${e.id}`}>
                              <div className="flex items-center gap-1">
                                {paid ? <CheckIcon size={9} /> : e.kind === "package" ? <PackageIcon size={9} /> : <Car size={9} />}
                                <span className="truncate font-medium">{e.name}</span>
                              </div>
                              <div className="truncate opacity-80">{e.title}</div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-6 mt-4 font-body text-xs p-mut">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-blue-500/40 inline-block" /> Traslado</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 inline-block" style={{ background: "var(--pbtn)", opacity: 0.5 }} /> Paquete</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-400/60 inline-block" /> Pagado</span>
            </div>
          </TabsContent>

          {/* GENERATE PAYMENT LINK */}
          <TabsContent value="generate" className="mt-6">
            <div className="max-w-3xl">
              <h2 className="font-heading text-2xl font-semibold mb-1">Generar link de pago</h2>
              <p className="font-body text-sm p-mut mb-6">Captura los datos del cliente y genera un link de pago (máximo $2,500 USD) con nombre, correo y teléfono pre-llenados.</p>
              <div className="p-card p-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className={glLabel}>Nombre de cliente *</label>
                    <Input data-testid="gl-name" value={gl.customer_name} onChange={setGlField("customer_name")} className={glInput} />
                  </div>
                  <div>
                    <label className={glLabel}>Adultos</label>
                    <Select value={gl.adults} onValueChange={(v) => setGl({ ...gl, adults: v })}>
                      <SelectTrigger data-testid="gl-adults" className={glInput}><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-60">{range(1, 50).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={glLabel}>Menores</label>
                    <Select value={gl.minors} onValueChange={(v) => setGl({ ...gl, minors: v })}>
                      <SelectTrigger data-testid="gl-minors" className={glInput}><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-60">{range(0, 50).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={glLabel}>Infantes</label>
                    <Select value={gl.infants} onValueChange={(v) => setGl({ ...gl, infants: v })}>
                      <SelectTrigger data-testid="gl-infants" className={glInput}><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-60">{range(0, 20).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><label className={glLabel}>Transportación (opcional)</label><Input data-testid="gl-transport" value={gl.transport} onChange={setGlField("transport")} placeholder="Ej. SUV, Van..." className={glInput} /></div>
                  <div><label className={glLabel}>Correo *</label><Input data-testid="gl-email" type="email" value={gl.customer_email} onChange={setGlField("customer_email")} className={glInput} /></div>
                  <div><label className={glLabel}>Teléfono *</label><Input data-testid="gl-phone" value={gl.customer_phone} onChange={setGlField("customer_phone")} placeholder="+52..." className={glInput} /></div>
                  <div><label className={glLabel}>Check In</label><Input data-testid="gl-checkin" type="date" value={gl.check_in} onChange={setGlField("check_in")} className={glInput} /></div>
                  <div><label className={glLabel}>Check Out</label><Input data-testid="gl-checkout" type="date" value={gl.check_out} onChange={setGlField("check_out")} className={glInput} /></div>
                  <div className="sm:col-span-2">
                    <label className={glLabel}>Servicios adicionales (opcional)</label>
                    <Textarea data-testid="gl-additional" value={gl.additional_services} onChange={setGlField("additional_services")} rows={2} className={glInput} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={glLabel}>Monto a cobrar (USD) * — máx. 2,500</label>
                    <Input data-testid="gl-amount" type="number" min="1" max="2500" value={gl.amount} onChange={setGlField("amount")} className={glInput} />
                  </div>
                  <div>
                    <label className={glLabel}>Idioma de la pantalla de pago</label>
                    <Select value={gl.lang} onValueChange={(v) => setGl({ ...gl, lang: v })}>
                      <SelectTrigger data-testid="gl-lang" className={glInput}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="es">Español</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <button onClick={generateLink} disabled={glLoading} data-testid="gl-submit" className="p-btn mt-6 inline-flex items-center gap-2 font-body px-8 py-3.5 transition-all disabled:opacity-60">
                  {glLoading ? <Loader2 size={18} className="animate-spin" /> : <Link2 size={18} />}
                  {glLoading ? "Generando..." : "Generar link de pago"}
                </button>

                {glLink && (
                  <div className="mt-6 p-4" style={{ border: "1px solid color-mix(in srgb, var(--pbtn) 40%, transparent)", background: "color-mix(in srgb, var(--pbtn) 6%, transparent)" }} data-testid="gl-result">
                    <p className="font-body text-xs uppercase tracking-wider p-accent mb-2 flex items-center gap-1.5"><CheckIcon size={14} /> Link generado — compártelo con tu cliente</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input readOnly value={glLink} data-testid="gl-link-value" className="p-input flex-1 font-body text-xs px-3 py-2.5 truncate border" />
                      <button onClick={copyLink} data-testid="gl-copy" className="inline-flex items-center justify-center gap-2 border p-line px-4 py-2.5 font-body text-sm hover:opacity-70 transition-opacity"><Copy size={15} /> Copiar</button>
                      <a href={glLink} target="_blank" rel="noreferrer" data-testid="gl-open" className="p-btn inline-flex items-center justify-center gap-2 px-4 py-2.5 font-body text-sm">Abrir</a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* EMAIL TEMPLATES */}
          <TabsContent value="templates" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h2 className="font-heading text-2xl font-semibold mb-1">Plantilla del correo de confirmación</h2>
                <p className="font-body text-sm p-mut mb-5">Edita textos, información y colores del correo que recibe el cliente al confirmarse su pago. Usa variables que se reemplazan automáticamente.</p>
                <div className="p-card p-6 space-y-4">
                  <div><label className={glLabel}>Asunto</label><Input data-testid="tpl-subject" value={tpl.subject} onChange={setTplField("subject")} className={glInput} /></div>
                  <div><label className={glLabel}>Título</label><Input data-testid="tpl-heading" value={tpl.heading} onChange={setTplField("heading")} className={glInput} /></div>
                  <div><label className={glLabel}>Mensaje de introducción</label><Textarea data-testid="tpl-intro" value={tpl.intro} onChange={setTplField("intro")} rows={3} className={glInput} /></div>
                  <div><label className={glLabel}>Mensaje de cierre</label><Textarea data-testid="tpl-closing" value={tpl.closing} onChange={setTplField("closing")} rows={3} className={glInput} /></div>
                  <div><label className={glLabel}>Nombre de marca (banner del correo)</label><Input data-testid="tpl-brand" value={tpl.brand_name} onChange={setTplField("brand_name")} className={glInput} /></div>
                  <div><label className={glLabel}>Pie de página</label><Input data-testid="tpl-footer" value={tpl.footer} onChange={setTplField("footer")} className={glInput} /></div>
                  <div className="flex items-center gap-3 border-t p-line pt-4">
                    <input type="checkbox" id="tpl-show-logo" checked={tpl.show_logo} onChange={(e) => setTpl({ ...tpl, show_logo: e.target.checked })} data-testid="tpl-show-logo" className="w-4 h-4 cursor-pointer" />
                    <label htmlFor="tpl-show-logo" className="font-body text-sm cursor-pointer">Mostrar logo en el correo</label>
                  </div>
                  {tpl.show_logo && (
                    <div><label className={glLabel}>URL del logo (https://)</label><Input data-testid="tpl-logo-url" value={tpl.logo_url} onChange={setTplField("logo_url")} placeholder={DEFAULT_LOGO} className={glInput + " font-mono text-xs"} /></div>
                  )}
                  <div className="border-t p-line pt-4 space-y-3">
                    <p className="font-body text-xs uppercase tracking-wider p-mut">Colores del correo</p>
                    <ColorField label="Fondo del correo" value={tpl.email_bg} onChange={(v) => setTpl({ ...tpl, email_bg: v })} testid="tpl-color-email-bg" />
                    <ColorField label="Fondo de la tarjeta" value={tpl.card_bg} onChange={(v) => setTpl({ ...tpl, card_bg: v })} testid="tpl-color-card-bg" />
                    <ColorField label="Fondo del banner" value={tpl.banner_bg} onChange={(v) => setTpl({ ...tpl, banner_bg: v })} testid="tpl-color-banner-bg" />
                    <ColorField label="Texto del banner" value={tpl.banner_text} onChange={(v) => setTpl({ ...tpl, banner_text: v })} testid="tpl-color-banner-text" />
                    <ColorField label="Color de títulos" value={tpl.heading_color} onChange={(v) => setTpl({ ...tpl, heading_color: v })} testid="tpl-color-heading" />
                    <ColorField label="Color de texto" value={tpl.text_color} onChange={(v) => setTpl({ ...tpl, text_color: v })} testid="tpl-color-text" />
                    <ColorField label="Color de acento" value={tpl.accent_color} onChange={(v) => setTpl({ ...tpl, accent_color: v })} testid="tpl-color-accent" />
                  </div>
                  <div className="p-card p-3">
                    <p className="font-body text-xs p-mut mb-2">Variables disponibles (se reemplazan con los datos del cliente):</p>
                    <div className="flex flex-wrap gap-2">
                      {["{nombre}", "{monto}", "{huespedes}", "{checkin}", "{checkout}", "{servicio}"].map((v) => (
                        <span key={v} className="font-mono text-[11px] p-accent px-2 py-1 rounded" style={{ background: "color-mix(in srgb, var(--pbtn) 12%, transparent)" }}>{v}</span>
                      ))}
                    </div>
                  </div>
                  <button onClick={saveTemplate} disabled={tplSaving} data-testid="tpl-save" className="p-btn inline-flex items-center gap-2 font-body px-8 py-3.5 transition-all disabled:opacity-60">
                    {tplSaving ? <Loader2 size={18} className="animate-spin" /> : <CheckIcon size={18} />}
                    {tplSaving ? "Guardando..." : "Guardar plantilla"}
                  </button>
                </div>
              </div>

              {/* Live preview */}
              <div>
                <p className="font-body text-xs uppercase tracking-wider p-mut mb-3">Vista previa (datos de ejemplo)</p>
                <div className="border p-line overflow-hidden" style={{ background: tpl.email_bg }} data-testid="tpl-preview">
                  <div className="overflow-hidden border p-line m-4" style={{ background: tpl.card_bg }}>
                    {tpl.show_logo && (
                      <div className="bg-white px-6 pt-5 pb-2 text-center border-b p-line">
                        <img src={tpl.logo_url || DEFAULT_LOGO} alt={tpl.brand_name} className="h-16 inline-block object-contain" />
                      </div>
                    )}
                    <div className="px-6 py-4 text-center" style={{ background: tpl.banner_bg }}>
                      <span className="font-heading text-lg font-semibold" style={{ color: tpl.banner_text }}>{tpl.brand_name || "XCARET MEXICO"}</span>
                    </div>
                    <div className="p-6">
                      <div className="text-[11px] mb-3 font-body" style={{ color: tpl.text_color }}>Asunto: <span className="font-semibold" style={{ color: tpl.heading_color }}>{fillSample(tpl.subject)}</span></div>
                      <h3 className="font-heading text-xl font-semibold mb-2" style={{ color: tpl.heading_color }}>{fillSample(tpl.heading)}</h3>
                      <p className="font-body text-sm mb-4 whitespace-pre-line" style={{ color: tpl.text_color }}>{fillSample(tpl.intro)}</p>
                      <div className="py-3 mb-4 space-y-1.5" style={{ borderTop: `1px solid ${tpl.accent_color}`, borderBottom: `1px solid ${tpl.accent_color}` }}>
                        {[["Servicio", tplSample.servicio], ["Huéspedes", tplSample.huespedes], ["Check In", tplSample.checkin], ["Check Out", tplSample.checkout], ["Total pagado", tplSample.monto]].map(([l, v]) => (
                          <div key={l} className="flex justify-between font-body text-xs"><span style={{ color: tpl.text_color }}>{l}</span><span className="font-semibold" style={{ color: tpl.heading_color }}>{v}</span></div>
                        ))}
                      </div>
                      <p className="font-body text-sm whitespace-pre-line" style={{ color: tpl.text_color }}>{fillSample(tpl.closing)}</p>
                    </div>
                    <div className="px-6 py-4 border-t p-line"><p className="text-[11px] font-body" style={{ color: tpl.text_color }}>{tpl.footer}</p></div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* USERS (admin only) */}
          {isAdmin && (
          <TabsContent value="users" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <h2 className="font-heading text-2xl font-semibold mb-1">Crear usuario de cajero</h2>
                <p className="font-body text-sm p-mut mb-5">Los cajeros pueden generar links, editar plantillas y ver únicamente sus propios pagos.</p>
                <div className="p-card p-6 space-y-4">
                  <div><label className={glLabel}>Nombre</label><Input data-testid="user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className={glInput} /></div>
                  <div><label className={glLabel}>Correo</label><Input data-testid="user-email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className={glInput} /></div>
                  <div><label className={glLabel}>Contraseña (mín. 6)</label><Input data-testid="user-password" type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className={glInput} /></div>
                  <div>
                    <label className={glLabel}>Rol</label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger data-testid="user-role" className={glInput}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="cashier">Cajero</SelectItem><SelectItem value="admin">Administrador</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <button onClick={createUser} data-testid="user-create" className="p-btn inline-flex items-center gap-2 font-body px-8 py-3.5 transition-all">Crear usuario</button>
                </div>
              </div>
              <div>
                <p className="font-body text-xs uppercase tracking-wider p-mut mb-3">Usuarios ({users.length})</p>
                <div className="space-y-3">
                  {users.map((u) => (
                    <div key={u.id} className="p-card p-4" data-testid={`user-row-${u.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-body font-semibold truncate">{u.name} <span className="text-xs uppercase p-accent ml-1">{u.role === "admin" ? "Admin" : "Cajero"}</span></div>
                          <div className="font-body text-xs p-mut truncate">{u.email}</div>
                        </div>
                        {u.role !== "admin" && (
                          <button onClick={() => removeUser(u.id)} data-testid={`user-delete-${u.id}`} className="text-xs border border-red-500/50 text-red-600 px-3 py-1.5 hover:bg-red-500/10 transition-colors shrink-0">Eliminar</button>
                        )}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Input data-testid={`user-newpw-${u.id}`} type="text" placeholder="Nueva contraseña" value={pwUser[u.id] || ""} onChange={(e) => setPwUser({ ...pwUser, [u.id]: e.target.value })} className="p-input text-xs h-9" />
                        <button onClick={() => changePassword(u.id)} data-testid={`user-setpw-${u.id}`} className="text-xs border p-line px-3 py-1.5 hover:opacity-70 transition-opacity shrink-0">Cambiar</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
          )}

          {/* PANEL COLORS */}
          <TabsContent value="color" className="mt-6">
            <div className="max-w-xl">
              <h2 className="font-heading text-2xl font-semibold mb-1">Colores del panel</h2>
              <p className="font-body text-sm p-mut mb-5">Personaliza el panel a tu gusto. Los cambios se ven al instante y se guardan solo para tu usuario.</p>
              <div className="p-card p-6 space-y-4">
                <ColorField label="Fondo de página" value={ps.page_bg} onChange={(v) => setPs({ ...ps, page_bg: v })} testid="color-page-bg" />
                <ColorField label="Fondo de botones" value={ps.button_bg} onChange={(v) => setPs({ ...ps, button_bg: v })} testid="color-button-bg" />
                <ColorField label="Color de letras" value={ps.text_color} onChange={(v) => setPs({ ...ps, text_color: v })} testid="color-text" />
                <ColorField label="Color de banner (barra superior)" value={ps.banner_bg} onChange={(v) => setPs({ ...ps, banner_bg: v })} testid="color-banner-bg" />
                <div className="flex gap-3 pt-2">
                  <button onClick={savePanelSettings} disabled={psSaving} data-testid="color-save" className="p-btn inline-flex items-center gap-2 font-body px-8 py-3 transition-all disabled:opacity-60">
                    {psSaving ? <Loader2 size={16} className="animate-spin" /> : <CheckIcon size={16} />}
                    {psSaving ? "Guardando..." : "Guardar colores"}
                  </button>
                  <button onClick={() => setPs(PANEL_DEFAULTS)} data-testid="color-reset" className="inline-flex items-center gap-2 border p-line px-6 py-3 font-body text-sm hover:opacity-70 transition-opacity">Restablecer</button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
